import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { LancamentoFinanceiro, PaginatedResponse, TipoTransacao, StatusLancamento } from '../../../types';

const LancamentosList: React.FC = () => {
  const navigate = useNavigate();
  const [lancamentos, setLancamentos] = useState<LancamentoFinanceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [tipoFilter, setTipoFilter] = useState<TipoTransacao | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusLancamento | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const loadLancamentos = async () => {
      try {
        setLoading(true);
        const params: Record<string, any> = {
          page: currentPage,
          page_size: pageSize,
        };

        if (tipoFilter) {
          params.tipo = tipoFilter;
        }
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (searchTerm) {
          params.search = searchTerm;
        }

        const response = await financeiroApi.getLancamentos(params);
        setLancamentos(response.results);
        setTotalCount(response.count || 0);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar lançamentos');
        console.error('Error loading lancamentos:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLancamentos();
  }, [currentPage, pageSize, tipoFilter, statusFilter, searchTerm]);

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(dateString));
  };

  const getTipoBadgeColor = (tipo: TipoTransacao | undefined) => {
    switch (tipo) {
      case 'R':
        return 'bg-green-100 text-green-800';
      case 'D':
        return 'bg-red-100 text-red-800';
      case 'T':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoLabel = (tipo: TipoTransacao | undefined) => {
    switch (tipo) {
      case 'R':
        return 'Receita';
      case 'D':
        return 'Despesa';
      case 'T':
        return 'Transferência';
      default:
        return 'N/A';
    }
  };

  const getStatusBadgeColor = (status: StatusLancamento | undefined) => {
    switch (status) {
      case 'P':
        return 'bg-yellow-100 text-yellow-800';
      case 'C':
        return 'bg-green-100 text-green-800';
      case 'CA':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: StatusLancamento | undefined) => {
    switch (status) {
      case 'P':
        return 'Pendente';
      case 'C':
        return 'Concluído';
      case 'CA':
        return 'Cancelado';
      default:
        return 'N/A';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const clearFilters = () => {
    setSearchTerm('');
    setTipoFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  if (loading && lancamentos.length === 0) {
    return (
    <div className="space-y-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Lançamentos Financeiros</h1>
          <p className="text-xs text-secondary-600 mt-0.5">
            Gerencie receitas, despesas e transferências
          </p>
        </div>
        <Link
          to="/financeiro/lancamentos/new"
          className="btn-primary flex items-center whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Novo Lançamento
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Buscar por descrição ou número..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Tipo Filter */}
          <div>
            <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              id="tipo"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={tipoFilter}
              onChange={(e) => {
                setTipoFilter(e.target.value as TipoTransacao | '');
                setCurrentPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="R">Receita</option>
              <option value="D">Despesa</option>
              <option value="T">Transferência</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusLancamento | '');
                setCurrentPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="P">Pendente</option>
              <option value="C">Concluído</option>
              <option value="CA">Cancelado</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-500 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          {lancamentos.length > 0 ? (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vencimento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {lancamentos.map((lancamento) => (
                  <tr key={lancamento.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{lancamento.numero}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap max-w-xs">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900 truncate">{lancamento.descricao}</span>
                        {lancamento.categoria_nome && (
                          <span className="text-xs text-gray-500">{lancamento.categoria_nome}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeColor(
                          lancamento.tipo
                        )}`}
                      >
                        {getTipoLabel(lancamento.tipo)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span
                        className={`text-sm font-medium ${
                          lancamento.tipo === 'R' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {lancamento.tipo === 'R' ? '+' : '-'}
                        {formatCurrency(lancamento.valor)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatDate(lancamento.data_vencimento)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                          lancamento.status
                        )}`}
                      >
                        {getStatusLabel(lancamento.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum lançamento encontrado</h3>
              <p className="mt-1 text-xs text-gray-500">
                Tente ajustar os filtros para encontrar lançamentos.
              </p>
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        {lancamentos.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Mostrando {(currentPage - 1) * pageSize + 1} a{' '}
              {Math.min(currentPage * pageSize, totalCount)} de {totalCount} lançamentos
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LancamentosList;
